import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router'
import moment from 'moment'
import clsx from 'clsx'
import { Base } from '@itsrever/design-system'
import {
  SizingModelListItem,
  SizingModels,
  SizingStatus
} from '@/domain/sizing'
import { formatCountries, formatCurrency } from '@/utils'
import { formatPercent } from '@/utils/format'
import { ProgressBar, ProgressLabel, StatusPill } from './primitives'

const { EmptyState, Table, ToggleGroup, ToggleGroupItem, createColumnHelper } =
  Base

const columnHelper = createColumnHelper<SizingModelListItem>()

type StatusFilter = 'ALL' | 'NEW' | SizingStatus

const FILTERS: { value: StatusFilter; key: string }[] = [
  { value: 'ALL', key: 'sizing.models.filter_all' },
  { value: 'NEW', key: 'sizing.status.new' },
  { value: 'WORKING', key: 'sizing.status.working' },
  { value: 'COLLECTING_DATA', key: 'sizing.status.collecting' },
  { value: 'NO_CLEAR_EFFECT', key: 'sizing.status.no_effect' },
  { value: 'REDIAGNOSIS', key: 'sizing.status.rediagnosis' },
  { value: 'RETIRED', key: 'sizing.status.retired' }
]

function shortDate(value: string | null, language: string) {
  if (!value) return null
  return moment(value).locale(language).format('D MMM YY')
}

// Nothing was billed: the measurement came to zero, or none was ever frozen for
// the row. Same rounding as the savings cell, which prints a dash for both.
function noSavings(model: SizingModelListItem) {
  return Math.round(model.savedEurToDate ?? 0) === 0
}

// Rates belong to the rows whose euros they explain: a banner that is working,
// or a retirement that ended up billing something. Anywhere else the movement
// is noise the measurement already refused to charge for.
function showsRates(model: SizingModelListItem) {
  if (noSavings(model)) return false
  return model.status === 'WORKING' || model.status === 'RETIRED'
}

// A retirement always says which kind it was: REVER pulled the notice, or the
// product left the catalogue and took its notice with it.
function retirementNote(model: SizingModelListItem): string[] {
  if (model.status !== 'RETIRED') return []
  return [
    model.catalogRetired
      ? 'sizing.models.catalog_retired_note'
      : 'sizing.models.retired_note'
  ]
}

function noteKeys(model: SizingModelListItem): string[] {
  const measurement =
    noSavings(model) || model.classification === 'NEUTRAL'
      ? 'sizing.models.neutral_note'
      : model.status === 'REDIAGNOSIS'
        ? 'sizing.models.rediagnosis_note'
        : 'sizing.models.no_effect_note'
  return [measurement, ...retirementNote(model)]
}

function matchesFilter(model: SizingModelListItem, filter: StatusFilter) {
  if (filter === 'ALL') return true
  // New models live in their own bucket so the chips partition the list.
  if (filter === 'NEW') return model.isNew
  return model.status === filter && !model.isNew
}

export function ModelsTab({ data }: { data: SizingModels }) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [filter, setFilter] = useState<StatusFilter>('ALL')

  const cycle = searchParams.get('cycle')
  const detailPath = useCallback(
    (modelKey: string) =>
      `/size-suggestions/${modelKey}${cycle ? `?cycle=${cycle}` : ''}`,
    [cycle]
  )

  const models = useMemo(
    () => data.models.filter((model) => matchesFilter(model, filter)),
    [data.models, filter]
  )

  const columns = useMemo(
    () => [
      columnHelper.accessor('modelName', {
        header: () => t('sizing.models.col_model'),
        cell: (info) => {
          const model = info.row.original
          return (
            <div className="flex items-center gap-3">
              {model.imageUrl ? (
                <img
                  src={model.imageUrl}
                  alt=""
                  className="h-11 w-9 flex-none rounded-md border border-solid border-neutral-200 object-cover"
                  // Deleted products lose their CDN file; a broken-image icon is worse than nothing.
                  onError={(event) => {
                    event.currentTarget.style.display = 'none'
                  }}
                />
              ) : null}
              <div>
                <p className="text-sm font-semibold">
                  {model.status === 'RETIRED' ? (
                    model.modelName
                  ) : (
                    <Link
                      to={detailPath(model.modelKey)}
                      onClick={(event) => event.stopPropagation()}
                      className="text-neutral-900 no-underline"
                    >
                      {model.modelName}
                    </Link>
                  )}
                </p>
                <p className="text-xs text-neutral-500">
                  {[
                    model.colorwayCount > 0
                      ? t('sizing.models.colorways', { n: model.colorwayCount })
                      : null,
                    model.category || null,
                    model.country !== 'WW'
                      ? formatCountries(model.country, i18n.language)
                      : null
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
            </div>
          )
        }
      }),
      columnHelper.accessor('sizeProblem', {
        header: () => t('sizing.models.col_notice'),
        cell: (info) => (
          <span className="whitespace-nowrap text-xs font-semibold tracking-wide text-neutral-600">
            {info.getValue() === 'TOO_BIG'
              ? t('sizing.models.direction_big')
              : t('sizing.models.direction_small')}
          </span>
        )
      }),
      columnHelper.accessor('status', {
        header: () => t('sizing.models.col_status'),
        cell: (info) => {
          const model = info.row.original
          return (
            <div className="min-w-32">
              <StatusPill status={model.status} isNew={model.isNew} />
              {model.status === 'COLLECTING_DATA' ? (
                <ProgressBar
                  value={model.progress.units}
                  target={model.progress.unitsTarget}
                />
              ) : null}
            </div>
          )
        }
      }),
      columnHelper.accessor('totalChangePct', {
        header: () => t('sizing.models.col_returns'),
        cell: (info) => {
          const model = info.row.original
          if (model.status === 'COLLECTING_DATA') {
            return <ProgressLabel progress={model.progress} />
          }
          if (!showsRates(model)) {
            return (
              <div>
                {noteKeys(model).map((key) => (
                  <p key={key} className="text-xs text-neutral-500">
                    {t(key)}
                  </p>
                ))}
              </div>
            )
          }
          if (
            model.totalChangePct === null ||
            model.rateBeforePct === null ||
            model.rateAfterPct === null
          ) {
            return (
              <div className="tabular-nums">
                <p className="text-sm font-semibold text-neutral-600">—</p>
                <p className="text-xs text-neutral-500">—</p>
                {retirementNote(model).map((key) => (
                  <p key={key} className="text-xs text-neutral-500">
                    {t(key)}
                  </p>
                ))}
              </div>
            )
          }
          const pct = (value: number) => formatPercent(value, i18n.language)
          return (
            <div className="tabular-nums">
              <p
                className={clsx(
                  'text-sm font-semibold',
                  model.totalChangePct < 0
                    ? 'text-success-dark'
                    : 'text-neutral-600'
                )}
              >
                {t('sizing.models.total_change', {
                  value: `${model.totalChangePct > 0 ? '+' : ''}${Math.round(
                    model.totalChangePct
                  )}%`
                })}
              </p>
              <p className="text-xs text-neutral-500">
                {/* Legacy freezes carry euros but no unit count; "(0 uds)"
                    would read as a measurement over nobody. */}
                {model.unitsAfter === null
                  ? t('sizing.models.rate_change_no_units', {
                      before: pct(model.rateBeforePct),
                      after: pct(model.rateAfterPct)
                    })
                  : t('sizing.models.rate_change', {
                      before: pct(model.rateBeforePct),
                      after: pct(model.rateAfterPct),
                      units: model.unitsAfter.toLocaleString(i18n.language)
                    })}
              </p>
              {retirementNote(model).map((key) => (
                <p key={key} className="text-xs text-neutral-500">
                  {t(key)}
                </p>
              ))}
            </div>
          )
        }
      }),
      columnHelper.accessor('savedEurToDate', {
        header: () => t('sizing.models.col_savings'),
        cell: (info) => {
          const value = info.getValue()
          // Rounded, because that is what the cell would print: a banner that
          // saved nothing and one that saved half a euro both read "0 €", and
          // a euro figure of zero is an absence, not an amount.
          if (value === null || Math.round(value) === 0) {
            return <span className="text-neutral-500">—</span>
          }
          return (
            <span className="text-sm font-semibold tabular-nums">
              {formatCurrency(value.toFixed(0), 'EUR', {
                lang: i18n.language,
                maximumFractionDigits: 0
              })}
            </span>
          )
        }
      }),
      columnHelper.accessor('activeFrom', {
        header: () => t('sizing.models.col_active_from'),
        cell: (info) => (
          <span className="whitespace-nowrap text-xs tabular-nums text-neutral-600">
            {shortDate(info.getValue(), i18n.language) ?? '—'}
          </span>
        )
      })
    ],
    [t, i18n.language, detailPath]
  )

  if (data.models.length === 0) {
    return (
      <EmptyState
        variant="card"
        title={t('sizing.models.empty_title')}
        description={t('sizing.models.empty_description')}
        className="h-64"
      />
    )
  }

  return (
    <div>
      <ToggleGroup
        variant="button"
        aria-label={t('sizing.models.filter_label')}
        className="mb-3"
        value={[filter]}
        // Single choice: pressing the selected option again keeps it selected.
        onValueChange={(next) =>
          next.length > 0 && setFilter(next[0] as StatusFilter)
        }
      >
        {FILTERS.map(({ value, key }) => {
          const count = data.models.filter((model) =>
            matchesFilter(model, value)
          ).length
          if (value !== 'ALL' && count === 0) return null
          return (
            <ToggleGroupItem key={value} value={value}>
              {t(key)} ({count})
            </ToggleGroupItem>
          )
        })}
      </ToggleGroup>
      <Table
        data={models}
        columns={columns}
        rowProps={{
          onClick: (model) => {
            if (model.status !== 'RETIRED') {
              navigate(detailPath(model.modelKey))
            }
          }
        }}
      />
    </div>
  )
}
